import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getArticleByChapterId } from "../../State/Article/Action";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Container,
  Button,
  Stack,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";

const ShowArticle = ({ chapterId: propChapterId }) => {
  const { chapterId: routeChapterId } = useParams();
  const chapterId = propChapterId || routeChapterId;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { article, loadingGet, errorGet } = useSelector(
    (state) => state.article
  );

  useEffect(() => {
    dispatch(getArticleByChapterId(chapterId));
  }, [dispatch, chapterId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f3e7e9 0%, #e3eeff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 5,
      }}
    >
      <Container maxWidth="md">
        {loadingGet ? (
          <Box textAlign="center">
            <CircularProgress size={50} color="primary" />
            <Typography variant="h6" mt={2}>
              Loading Article...
            </Typography>
          </Box>
        ) : errorGet ? (
          <Paper elevation={3} sx={{ p: 4, textAlign: "center", color: "red" }}>
            <ErrorOutlineIcon sx={{ fontSize: 40 }} color="error" />
            <Typography variant="h6" mt={2}>
              Failed to load article.
            </Typography>
            <Typography>
              {errorGet.message || "Something went wrong."}
            </Typography>
          </Paper>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 4,
                borderRadius: 4,
                background: "rgba(255, 255, 255, 0.85)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <MenuBookIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h4" fontWeight="bold">
                  Article
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  fontSize: "1.1rem",
                  fontFamily: "Georgia, serif",
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    article?.data || "No content available for this article."
                  ),
                }}
              />

              {/* Action Buttons */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                mt={4}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/edit-article/${chapterId}`)}
                >
                  Edit
                </Button>
              </Stack>
            </Paper>
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default ShowArticle;
